/**
 * ─────────────────────────────────────────────────────────────────────────────
 * AgroCast — ThemeToggle Component
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Variants:
 *  <ThemeToggle />               — icon button (default)
 *  <ThemeToggle variant="pill" /> — pill with label
 *  <ThemeToggle variant="menu" /> — dropdown: Light / Dark / System
 *
 * Features:
 *  - Framer Motion rotation + scale + fade animations
 *  - 200–300ms transition duration
 *  - Accessible: aria-label, aria-pressed, keyboard support
 *  - Glassmorphism menu panel in dropdown variant
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence }     from 'framer-motion'
import { Sun, Moon, Monitor, Check }   from 'lucide-react'
import { useTheme }                    from './useTheme'
import { THEMES }                      from './tokens'

// ── Animation Variants ────────────────────────────────────────────────────────

const iconVariants = {
  initial:  { opacity: 0, rotate: -90, scale: 0.5 },
  animate:  { opacity: 1, rotate: 0,   scale: 1,
              transition: { duration: 0.25, ease: [0.34, 1.56, 0.64, 1] } },
  exit:     { opacity: 0, rotate:  90, scale: 0.5,
              transition: { duration: 0.2,  ease: 'easeIn' } },
}

const menuVariants = {
  hidden:  { opacity: 0, scale: 0.95, y: -8,
             transition: { duration: 0.15, ease: 'easeIn' } },
  visible: { opacity: 1, scale: 1,    y: 0,
             transition: { duration: 0.2,  ease: [0.34, 1.56, 0.64, 1] } },
}

// ── Icon Map ──────────────────────────────────────────────────────────────────

function ThemeIcon({ theme, size = 18 }) {
  const icons = {
    [THEMES.LIGHT]:  <Sun  size={size} strokeWidth={1.75} />,
    [THEMES.DARK]:   <Moon size={size} strokeWidth={1.75} />,
    [THEMES.SYSTEM]: <Monitor size={size} strokeWidth={1.75} />,
  }
  return icons[theme] ?? icons[THEMES.LIGHT]
}

// ── Base Button Styles ────────────────────────────────────────────────────────

const btnBase = [
  'relative inline-flex items-center justify-center',
  'rounded-xl cursor-pointer select-none outline-none',
  'transition-colors duration-200',
  'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-offset-2',
  'focus-visible:ring-offset-[var(--ac-bg-base)]',
].join(' ')

// ── Variant: Icon Button ──────────────────────────────────────────────────────

function IconToggle({ size, className }) {
  const { isDark, toggleTheme, resolvedTheme } = useTheme()

  return (
    <motion.button
      type="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-pressed={isDark}
      onClick={toggleTheme}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.92 }}
      className={[
        btnBase,
        'w-10 h-10',
        'bg-[var(--ac-bg-subtle)] hover:bg-[var(--ac-bg-surface)]',
        'border border-[var(--ac-border-base)] hover:border-[var(--ac-border-strong)]',
        'text-[var(--ac-text-muted)] hover:text-[var(--ac-text-base)]',
        className,
      ].filter(Boolean).join(' ')}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={resolvedTheme}
          variants={iconVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="flex items-center justify-center"
        >
          <ThemeIcon theme={resolvedTheme} size={size ?? 18} />
        </motion.span>
      </AnimatePresence>
    </motion.button>
  )
}

// ── Variant: Pill Toggle ──────────────────────────────────────────────────────

function PillToggle({ className }) {
  const { isDark, toggleTheme, resolvedTheme } = useTheme()
  const label = isDark ? 'Dark' : 'Light'

  return (
    <motion.button
      type="button"
      aria-label={`Current theme: ${label}. Click to toggle.`}
      onClick={toggleTheme}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      className={[
        btnBase,
        'gap-2 px-3 h-9 text-sm font-medium',
        'bg-[var(--ac-bg-subtle)] hover:bg-[var(--ac-bg-surface)]',
        'border border-[var(--ac-border-base)] hover:border-[var(--ac-secondary)]',
        'text-[var(--ac-text-muted)] hover:text-[var(--ac-text-base)]',
        className,
      ].filter(Boolean).join(' ')}
    >
      {/* Track */}
      <div className="relative w-8 h-4 rounded-full bg-[var(--ac-border-strong)] overflow-hidden">
        <motion.div
          className="absolute inset-0 rounded-full bg-[var(--ac-secondary)]"
          animate={{ opacity: isDark ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.div
          className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm"
          animate={{ left: isDark ? 'calc(100% - 14px)' : '2px' }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      </div>

      {/* Icon + Label */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={resolvedTheme}
          variants={iconVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="flex items-center gap-1.5"
        >
          <ThemeIcon theme={resolvedTheme} size={14} />
          <span>{label}</span>
        </motion.span>
      </AnimatePresence>
    </motion.button>
  )
}

// ── Variant: Menu (Light / Dark / System) ─────────────────────────────────────

const menuOptions = [
  { value: THEMES.LIGHT,  label: 'Light',  Icon: Sun },
  { value: THEMES.DARK,   label: 'Dark',   Icon: Moon },
  { value: THEMES.SYSTEM, label: 'System', Icon: Monitor },
]

function MenuToggle({ className }) {
  const { preference, setTheme, resolvedTheme, glassClasses } = useTheme()
  const [open, setOpen]   = useState(false)
  const containerRef      = useRef(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  return (
    <div ref={containerRef} className={`relative inline-block ${className ?? ''}`}>
      {/* Trigger */}
      <motion.button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Select theme"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={[
          btnBase,
          'w-10 h-10',
          'bg-[var(--ac-bg-subtle)] hover:bg-[var(--ac-bg-surface)]',
          'border border-[var(--ac-border-base)] hover:border-[var(--ac-border-strong)]',
          'text-[var(--ac-text-muted)] hover:text-[var(--ac-text-base)]',
        ].join(' ')}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={resolvedTheme}
            variants={iconVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="flex items-center justify-center"
          >
            <ThemeIcon theme={preference} size={18} />
          </motion.span>
        </AnimatePresence>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className={[
              glassClasses,
              'absolute right-0 top-12 z-[var(--ac-z-overlay)]',
              'min-w-[140px] rounded-xl p-1.5',
            ].join(' ')}
          >
            {menuOptions.map(({ value, label, Icon }) => {
              const isActive = preference === value
              return (
                <motion.button
                  key={value}
                  role="menuitem"
                  type="button"
                  aria-checked={isActive}
                  onClick={() => { setTheme(value); setOpen(false) }}
                  whileHover={{ x: 2 }}
                  className={[
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm',
                    'transition-colors duration-150 cursor-pointer outline-none',
                    isActive
                      ? 'bg-[var(--ac-secondary)] text-[var(--ac-secondary-fg)] font-medium'
                      : 'text-[var(--ac-text-muted)] hover:bg-[var(--ac-bg-subtle)] hover:text-[var(--ac-text-base)]',
                  ].join(' ')}
                >
                  <Icon size={14} strokeWidth={1.75} />
                  <span className="flex-1 text-left">{label}</span>
                  {isActive && <Check size={12} strokeWidth={2.5} />}
                </motion.button>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Public Export ─────────────────────────────────────────────────────────────

/**
 * ThemeToggle — polymorphic theme switcher.
 *
 * @param {'icon' | 'pill' | 'menu'} variant — default 'icon'
 * @param {number} size — icon size in px (icon variant only)
 * @param {string} className — additional Tailwind classes
 *
 * @example
 * <ThemeToggle />
 * <ThemeToggle variant="pill" />
 * <ThemeToggle variant="menu" />
 */
export function ThemeToggle({ variant = 'icon', size, className }) {
  switch (variant) {
    case 'pill': return <PillToggle className={className} />
    case 'menu': return <MenuToggle className={className} />
    default:     return <IconToggle size={size} className={className} />
  }
}
